import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '@/hooks/useTasks';
import TaskListItem from './TaskListItem';
import TaskCreationStatus from './TaskCreationStatus';
import SetFilter from './SetFilter';
import Spinner from '@/components/common/Spinner';
import Button from '@/components/common/Button';
import { useGenerateTasks } from '@/hooks/useTasks';

export default function TaskList() {
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

  const { data, isLoading, error } = useTasks('active', selectedSetId);
  const navigate = useNavigate();
  const generateTasks = useGenerateTasks();

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-200 p-4">
        <h2 className="text-sm font-semibold text-neutral-700">Active Moves</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/app/tasks/history')}
        >
          History
        </Button>
      </div>

      {/* Set filter */}
      <div className="flex items-center gap-2 border-b border-neutral-100 px-4 py-2">
        <SetFilter selectedSetId={selectedSetId} onSetChange={setSelectedSetId} />
      </div>

      {/* Move list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <Spinner className="py-8" />}

        {error && (
          <div className="p-4 text-center text-sm text-error-600">
            Failed to load Moves
          </div>
        )}

        {data && data.tasks.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center px-4 py-12">
            <svg className="h-12 w-12 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>

            {data.task_creation_status === 'max_lifetime_tasks' && (
              <div className="mt-4 flex items-center gap-2">
                <Spinner size="sm" />
                <p className="text-sm text-neutral-600">
                <a href="/app/subscription">You've reached the maximum number of Moves for the free plan. Upgrade your subscription to access more Moves.</a>
                </p>
              </div>
            )}

            {data.task_creation_status === 'generation_in_progress' && (
              <div className="mt-4 flex items-center gap-2">
                <Spinner size="sm" />
                <p className="text-sm text-neutral-600">Jerry is creating Moves for you. This may take several minutes...</p>
              </div>
            )}

            {data.task_creation_status === 'can_generate' && (
              <div className="mt-4 text-center">
                <p className="mb-3 text-sm text-neutral-500">No active Moves</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => generateTasks.mutate()}
                  loading={generateTasks.isPending}
                  disabled={generateTasks.isPending}
                >
                  Create new Moves
                </Button>
              </div>
            )}

            {data.task_creation_status === 'max_active_tasks' && (
              <p className="mt-4 text-sm text-neutral-500">No active Moves</p>
            )}

            {data.task_creation_status === 'max_daily_tasks' && (
              <div className="mt-4 text-center">
                <p className="text-sm text-neutral-500">No active Moves</p>
                <p className="mt-1 text-xs text-neutral-400">
                  Daily Move generation limit reached. New Moves will be available tomorrow.
                </p>
              </div>
            )}
          </div>
        )}

        {data?.tasks.map((task) => (
          <TaskListItem
            key={task.task_id}
            task={task}
            onClick={() => navigate(`/app/tasks/${task.task_id}`)}
            showSetName={!selectedSetId}
          />
        ))}
      </div>

      {/* Move creation status footer — shown when there are moves */}
      {data && data.tasks.length > 0 && (
        <TaskCreationStatus status={data.task_creation_status} />
      )}
    </div>
  );
}
